import { useScreenNames } from "./useScreenNames"
import { TechDimensions } from "../dimensions/TechDimensions";
import { GeoDimensions } from "../dimensions/GeoDimensions";
import { SessionDimensions } from "../dimensions//SessionDimensions";
import { useLang } from "../../app/i18n/OpenSooqi18n";
import { getCookies, jsonSafeGet, getCurrentSessionCookieIdentity, getEmptyUtmData, getUtmDataFromUrl, hasUtmData, syncUtmDataWithCurrentSession, withoutDefaultUtmData } from "../../app/helpers/commonFunctions";
import { UserDimensions } from "../dimensions/UserDimensions";
import { DEFAULT_VALUE, UTM_DATA } from "../types/LoggingTypes";
import { useEffect, useMemo, useRef } from "react";
import Cookies from "js-cookie";
import { listenForMessages, sendMessage } from "../helpers/broadcastChannel";
import { extractCampaignPayloadFromSessionCampaign } from "../helpers/campaignPayload";



export const getQueryParams = () => {
    const query = new URLSearchParams(window.location.search);
    const hash = window.location.hash.replace(/^#\??/, "");
    const hashQuery = new URLSearchParams(hash);

    hashQuery.forEach((value, key) => {
        if (!query.has(key)) {
            query.set(key, value);
        }
    });

    return query;
}

type UtmDataBroadcastMessage = UTM_DATA & {
    sessionIdentity?: ReturnType<typeof getCurrentSessionCookieIdentity>
}

const getUtmDataBroadcastMessage = (utmData: UTM_DATA): UtmDataBroadcastMessage => ({
    ...utmData,
    sessionIdentity: getCurrentSessionCookieIdentity(),
})

const isSameSessionIdentity = (sessionIdentity?: ReturnType<typeof getCurrentSessionCookieIdentity>) => {
    const currentSession = getCurrentSessionCookieIdentity();

    if (!sessionIdentity || !currentSession) {
        return true;
    }

    return sessionIdentity.id === currentSession.id &&
        sessionIdentity.startedAt === currentSession.startedAt;
}

const isCurrentSessionUtmMessage = (message: UtmDataBroadcastMessage) => isSameSessionIdentity(message.sessionIdentity)

const setUtmDataInSessionDimensions = (message: UTM_DATA)=>{
    SessionDimensions.getInstance().sessionSource = message.cSource;
    SessionDimensions.getInstance().sessionCampaign = message.cCampaign;
    SessionDimensions.getInstance().sessionMedium = message.cMedium;
    SessionDimensions.getInstance().setUtmDataInStorage(message);
}

const useLogging = ()=>{

    const campaignPayload = useMemo(() =>
        extractCampaignPayloadFromSessionCampaign(Cookies.get('sessionCampaign')), []);
    const hasAppliedCampaignPayload = useRef(false);

    // Applied once from the session campaign cookie. Re-applying on later renders would put the
    // campaign back after a session rotation has already emptied it.
    if (campaignPayload && !hasAppliedCampaignPayload.current) {
        hasAppliedCampaignPayload.current = true;
        SessionDimensions.getInstance().sessionCampaignPayload = campaignPayload;
    }

    useScreenNames();
    
    useEffect(()=>{
        syncUtmDataWithCurrentSession();

        if (campaignPayload) {
            SessionDimensions.getInstance().sessionCampaignPayload = campaignPayload;
        }
        const storedUtmData = jsonSafeGet<Partial<UTM_DATA>>(sessionStorage.getItem('utmData'));
        const emptyUtmData: UTM_DATA = getEmptyUtmData();

        const urlParams = getUtmDataFromUrl();
        const utmData = withoutDefaultUtmData({
            cSource: storedUtmData?.cSource || '',
            cMedium: storedUtmData?.cMedium || '',
            cCampaign: storedUtmData?.cCampaign || '',
        });
        const hasCampaignParams = hasUtmData(urlParams);

        const loggingParams = hasCampaignParams ? urlParams : utmData;
        const loggingSessionIdentity = getCurrentSessionCookieIdentity();

        if(!hasCampaignParams && !hasUtmData(utmData))
        {
            setUtmDataInSessionDimensions(emptyUtmData);
            return listenForMessages((message: UtmDataBroadcastMessage) => {
                if (!isCurrentSessionUtmMessage(message)) {
                    return;
                }

                setUtmDataInSessionDimensions(message);
            });
        }
        setUtmDataInSessionDimensions(loggingParams)
        sendMessage(getUtmDataBroadcastMessage(loggingParams)); 
        const interval = setInterval(() => {
            if (!isSameSessionIdentity(loggingSessionIdentity)) {
                setUtmDataInSessionDimensions(emptyUtmData);
                sendMessage(getUtmDataBroadcastMessage(emptyUtmData));
                return;
            }

            sendMessage(getUtmDataBroadcastMessage(loggingParams));
        }, 1000)
        return () => clearInterval(interval);
    }, [campaignPayload])

    if(getCookies() && getCookies().ecountry !== undefined)
    {
        GeoDimensions.getInstance().geoAppCountry = getCookies().ecountry;
    }
    if(getCookies() && getCookies().userInfo && getCookies().userInfo.id)
    {   
        const userId = String(getCookies().userInfo.id ?? DEFAULT_VALUE);
        UserDimensions.getInstance().userMemberId = userId;
    }
    UserDimensions.getInstance().userTrackingId = (getCookies() && getCookies().device_uuid && getCookies().device_uuid.length > 0) ? getCookies().device_uuid : DEFAULT_VALUE;

    TechDimensions.getInstance().language = useLang() ?? DEFAULT_VALUE
    TechDimensions.getInstance().version = process.env.REACT_APP_PUBLIC_VERSION ?? DEFAULT_VALUE;
    
    SessionDimensions.getInstance().sessionId = getCookies().session?.id ? getCookies().session?.id : DEFAULT_VALUE;
}

export {useLogging}